<template>
  <div class="chapter-roast-page">
    <!-- Input state -->
    <div v-if="state === 'input'" class="state-input">
      <div class="hero">
        <h1>Get your chapter roasted. Free.</h1>
        <p class="subtitle">
          Paste a chapter below. We'll read it and deliver a sharp critique. Free, takes about a minute.
        </p>
      </div>

      <div class="trust-bar">
        <span class="trust-item"><QIcon :path="mdiCurrencyUsd" :size="16" /> Always free</span>
        <span class="trust-item"><QIcon :path="mdiClockOutline" :size="16" /> ~1 minute</span>
        <span class="trust-item"><QIcon :path="mdiShieldCheckOutline" :size="16" /> Never trains AI</span>
      </div>

      <div class="title-area">
        <input
          v-model="chapterTitle"
          type="text"
          placeholder="Chapter or manuscript title (optional)"
          class="title-input"
          maxlength="120"
          @input="userEditedTitle = true"
        />
      </div>

      <div class="paste-area">
        <textarea
          v-model="chapterText"
          placeholder="Paste your chapter here..."
          class="chapter-textarea"
          :class="{ 'over-limit': wordCount > MAX_WORDS, 'under-min': chapterText.length > 0 && wordCount < MIN_WORDS }"
          spellcheck="false"
        />
        <div class="word-count-bar">
          <span class="word-count" :class="wordCountClass">{{ wordCount.toLocaleString() }} words</span>
          <span v-if="wordCount > 0 && wordCount < MIN_WORDS" class="word-count-hint"
            >Minimum {{ MIN_WORDS.toLocaleString() }}</span
          >
          <span v-else-if="wordCount > MAX_WORDS" class="word-count-hint over"
            >Maximum {{ MAX_WORDS.toLocaleString() }}</span
          >
        </div>
      </div>

      <div class="submit-area">
        <button class="roast-btn" :disabled="!isValidWordCount || submitting" @click="submit">
          <span v-if="submitting">Starting...</span>
          <span v-else>Roast my chapter</span>
        </button>
        <p v-if="submitError" class="error-msg">{{ submitError }}</p>
        <p class="submit-legal">
          Twig will never use your writing to train AI. By submitting, you agree to our
          <a href="https://twig.io/terms-of-service" target="_blank">Terms of Service</a> and
          <a href="https://twig.io/privacy-policy" target="_blank">Privacy Policy</a>.
        </p>
      </div>

      <div class="reviews">
        <h2 class="reviews-heading">What writers are saying</h2>

        <div v-for="(row, rowIndex) in reviewRows" :key="rowIndex" class="marquee">
          <div class="marquee-track" :class="{ reverse: rowIndex === 1 }">
            <!-- rendered twice so the -50% translate loops without a seam -->
            <template v-for="pass in 2" :key="pass">
              <blockquote
                v-for="(r, i) in row"
                :key="`${pass}-${i}`"
                class="review-card"
                :aria-hidden="pass === 2 ? 'true' : 'false'"
              >
                <p class="review-quote">"{{ r.quote }}"</p>
                <cite class="review-name">{{ r.name }}</cite>
              </blockquote>
            </template>
          </div>
        </div>

        <p class="reviews-footnote">Hover to pause · real feedback from Twig writers</p>
      </div>

      <div class="faq">
        <h2 class="faq-heading">Have a Question?</h2>
        <div v-for="(item, i) in faqs" :key="i" class="faq-item" :class="{ open: openFaq === i }">
          <button class="faq-q" @click="openFaq = openFaq === i ? null : i">
            {{ item.q }}
            <span class="faq-chevron">{{ openFaq === i ? '−' : '+' }}</span>
          </button>
          <div class="faq-a">{{ item.a }}</div>
        </div>
      </div>
    </div>

    <!-- Processing state -->
    <div v-else-if="state === 'processing'" class="state-processing">
      <div class="processing-inner">
        <div class="t-wrapper">
          <svg class="t-mark" viewBox="-8 4 68 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M38.5653 98.715C36.6403 99.3309 34.2931 99.6398 31.522 99.6398C24.9008 99.6398 20.0711 97.9637 17.0311 94.6115C13.9893 91.2594 12.4703 86.0776 12.4703 79.0645V45.6586H0V40.6882L22.5157 14.7957H27.4807V40.3414H48.2643V45.6586H27.4807V79.9892C27.4807 86.0776 30.4827 89.121 36.4869 89.121C38.1034 89.121 39.6424 88.7941 41.1055 88.1385C42.5668 87.4846 44.454 86.3089 46.7633 84.6129L50.2272 89.4677L38.5653 98.715Z"
              fill="currentColor"
            />
          </svg>
          <div class="t-shadow"></div>
        </div>
        <Transition name="wisecrack" mode="out-in">
          <h2 :key="wiscrackIndex" class="wisecrack">{{ wisecracks[wiscrackIndex] }}</h2>
        </Transition>
        <div v-if="stepName" class="step-indicator">
          <span class="step-dot" />
          {{ stepName }}
        </div>
      </div>
    </div>

    <!-- Countdown state -->
    <div v-else-if="state === 'countdown'" class="state-countdown">
      <div class="countdown-inner">
        <p class="countdown-message">
          While the editor roasts your chapter, create your free account (or sign in if you already have one).
        </p>
        <p class="countdown-sub">
          Redirecting you in <strong>{{ countdown }}</strong
          >...
        </p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="state === 'error'" class="state-error">
      <h2>Something went wrong.</h2>
      <p>{{ fatalError }}</p>
      <button class="roast-btn" @click="reset">Try again</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { mdiClockOutline, mdiCurrencyUsd, mdiShieldCheckOutline } from '@mdi/js';
import QIcon from '../components/QIcon.vue';
import { track } from '../lib/analytics';
import { getAttribution, getTouches } from '../lib/attribution';
import { useAuthStore } from '../stores/auth';
import { countWords } from '../lib/word-count';

const MIN_WORDS = 500;
const MAX_WORDS = 5000;
const LS_KEY = 'pendingChapterRoastProjectId';
// The partner slug is only in the URL on the first hit. Submitting bounces the visitor
// through /auth, which drops the query string, so it has to be parked somewhere that
// survives the round trip. Cleared alongside LS_KEY so a later unrelated roast cannot
// inherit a stale partner.
const LS_VIA_KEY = 'pendingChapterRoastVia';

/** Reads `?via=` on first load and remembers it for the post-auth return trip. */
function captureVia() {
  const via = new URLSearchParams(window.location.search).get('via')?.trim().toLowerCase();
  if (via && /^[a-z0-9_-]{1,64}$/.test(via)) localStorage.setItem(LS_VIA_KEY, via);
}

function clearPending() {
  localStorage.removeItem(LS_KEY);
  localStorage.removeItem(LS_VIA_KEY);
}

const router = useRouter();
const authStore = useAuthStore();

type State = 'input' | 'countdown' | 'processing' | 'error';

const state = ref<State>('input');
const chapterText = ref('');
const chapterTitle = ref('');
const openFaq = ref<number | null>(null);

interface Review {
  quote: string;
  name: string;
}

// Seed copy, kept in the bundle as the fallback so the marquee is never empty if the
// testimonials request fails. The live list is curated in Admin → Feedback → Feature.
const fallbackReviews: Review[] = [
  {
    quote:
      'I love how it hits hard but accurate and yet gives useful feedback to make positive changes. Somehow having a humorous voice makes the constructive criticism easier to take.',
    name: 'Vanessa',
  },
  {
    quote:
      "Not only was it very funny, it had a lot of helpful constructive criticism. It didn't just point at the mistakes, it explained why they weren't working and how to fix them. Loved it!",
    name: 'Andrea',
  },
  {
    quote: 'Loved the roast. You could do stand-up comedy. It was helpful and laugh-out-loud funny.',
    name: 'Wendy P.',
  },
  {
    quote:
      'It gave me where to cut, what to add to make this a better scene. Love the roast. It is fun and constructive without being mean.',
    name: 'Barbara M.',
  },
  {
    quote:
      'Wow! This was detailed and helpful. Nice to get feedback that feels useful and brings a smile to your face.',
    name: 'Tali S.',
  },
  {
    quote: 'This was actually very useful and insightful — and funny. Nice tool!',
    name: 'Cean B.',
  },
  {
    quote:
      "I hadn't realized I duplicated a piece of a scene, or that the action was too 'soft.' Didn't realize how much it changed my character's personality. Thanks for that.",
    name: 'Robin Y.',
  },
  {
    quote: 'You gave me valuable, actionable advice. Thanks!',
    name: 'Jeanette D.',
  },
  {
    quote: 'I was very impressed with it, as well as the straight version of the analysis.',
    name: 'Donaco S.',
  },
  {
    quote:
      "I've used this to work through several versions. The roast keeps nailing me for summarizing rather than fleshing things out. That's real progress compared to earlier attempts.",
    name: 'W. Jordan',
  },
  {
    quote: "Very helpful, thank you. It's helpful to have what works pointed out, too.",
    name: 'Chani R.',
  },
  {
    quote:
      'Just tried it out and I honestly really liked it. It listed three great things, told me where I could improve, and did it all in a rather funny manner.',
    name: 'John K.',
  },
  {
    quote: 'A thorough roasting. Thanks, it will improve my writing.',
    name: 'A Twig writer',
  },
  {
    quote: "I'm genuinely impressed. It cut straight to the chase and helped me polish my work in minutes.",
    name: 'Fernanda G.',
  },
  {
    quote: 'Insightful stuff. Keep roasting.',
    name: 'Fiona L.',
  },
  {
    quote: "It's pretty cool, pretty weird, pretty scary what AI can do. I'm getting a lot out of this.",
    name: 'Megan H.',
  },
  {
    quote:
      'I loved the feedback it gave! It clarified some recommendations that other writers had given me as well. What a great tool!',
    name: 'Debora J.',
  },
  {
    quote: 'Thank you for bringing up possible changes. My editor can incorporate them.',
    name: 'Christine R.',
  },
  {
    quote: 'Fantastic. Delivered with punch and edginess. I can work with this!',
    name: 'A Twig writer',
  },
  {
    quote: 'I got a mirror put right in front of my face, and I needed that. I have made the adjustments. Thank you.',
    name: 'Errol B.',
  },
  {
    quote:
      'Great insight. I really like the understanding of what the chapter is about, and I respect the suggestions about how to tighten it. Excellent.',
    name: 'Joe B.',
  },
  {
    quote:
      "Very helpful. Sometimes you're so into a piece you can't see the wood for the trees. This highlighted a weakness I thought was a strength!",
    name: 'A Twig writer',
  },
  {
    quote: "Your analysis of what is wrong with the chapter was spot on, and I'd totally overlooked it.",
    name: 'T. Carr',
  },
  {
    quote: 'I CANNOT express how fantastic this all is. Simply cannot stop laughing. Tears. LOVE THIS!',
    name: 'A Twig writer',
  },
  {
    quote:
      "I love praise, but when I'm writing I need to be told where I'm going off track. Specific advice is helpful.",
    name: 'Jane B.',
  },
  {
    quote: 'So useful and insightful. It gives me a new sense of purpose with my writing and editing!',
    name: 'A Twig writer',
  },
  {
    quote: 'Dead on, calling me out on the overwriting. You nailed it. Perfectly!',
    name: 'D. Martelli',
  },
  {
    quote: "This gave me confidence and understanding to edit my novel, and to see what works and what doesn't.",
    name: 'Holly P.',
  },
  {
    quote: 'I love this Twig. It gets me, scolds me, and helps me write better in one snarky sentence.',
    name: 'A Twig writer',
  },
  {
    quote: 'Practical wisdom. Gotta stop weeding and feel the pain.',
    name: 'Ruth M.',
  },
];

const reviews = ref<Review[]>(fallbackReviews);

// Split into two rows that scroll in opposite directions
const reviewRows = computed(() => {
  const half = Math.ceil(reviews.value.length / 2);
  return [reviews.value.slice(0, half), reviews.value.slice(half)];
});

/** Curated testimonials come from D1 so quotes can be added without a deploy. */
async function loadReviews() {
  try {
    const response = await fetch('/api/testimonials/chapter-roast');
    if (!response.ok) return;
    const data = (await response.json()) as { testimonials?: Review[] };
    if (data.testimonials?.length) reviews.value = data.testimonials;
  } catch {
    // Keep the bundled fallback; an empty marquee is worse than a stale one.
  }
}

const faqs = [
  {
    q: 'Will this train AI on my writing?',
    a: 'No. Twig will never use your manuscript to train AI models.',
  },
  {
    q: 'Who can see my chapter?',
    a: 'Only you. Your chapter is stored to generate and serve your results, and is never shared.',
  },
  {
    q: 'How long do you keep my chapter roast?',
    a: "As long as you want it. Your roast lives on your Twig dashboard indefinitely, and we'll never use your writing to train AI. Want it gone? Delete it yourself from your dashboard. It's recoverable for 30 days if you email support@twig.io, then it's gone for good.",
  },
  {
    q: 'What kind of feedback will I get?',
    a: 'A playful, honest critique of your chapter — funnier than a normal edit, and a little meaner (all in good fun). We hope it makes you laugh and gives you something genuinely useful to work with.',
  },
  {
    q: 'How accurate is the feedback?',
    a: "Our goal is feedback that's accurate, funny, and ultimately useful — but just like any editor, human or not, Twig can make mistakes. Take what works for you and your book, and leave the rest.",
  },
  {
    q: 'Is this really free?',
    a: 'Yes!',
  },
  {
    q: 'What is Twig?',
    a: 'Twig is an AI-powered developmental editor that gives professional-level manuscript feedback in minutes instead of months, at roughly one-tenth the cost of a traditional developmental edit. The chapter roast is a free taste of what it can do.',
  },
];
const userEditedTitle = ref(false);
const projectId = ref<string | null>(null);
const submitting = ref(false);
const submitError = ref('');
const stepName = ref('');
const fatalError = ref('');

const countdown = ref(3);
let ws: WebSocket | null = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const wisecracks = [
  'Counting your adverbs. There are several.',
  'Checking if your characters have names AND personalities.',
  'Looking for the sentence that goes on a bit too long.',
  'Asking why the flashback was necessary.',
  'Your dialogue is being examined. Clinically.',
  'Finding the paragraph that could have been a sentence.',
  'Noting every time someone smiled sadly.',
  'Locating the exact moment the pacing went sideways.',
  'Reading your prose. Taking deep breaths.',
  'Tallying the filter words.',
  'Sharpening the critique.',
  'Selecting your most quotable line.',
  'Finding something genuinely good to say.',
  'Deciding how much to soften the blow.',
  'The editor is choosing their words carefully.',
  'Composing the part where we are honest with you.',
  'Preparing three things that are actually working.',
  'Writing the verdict.',
  'Almost done. We think you will like most of this.',
  'One last look. Then we tell you everything.',
];
const wiscrackIndex = ref(0);
let wiscrackTimer: ReturnType<typeof setInterval> | null = null;

watch(
  () => state.value,
  newState => {
    if (newState === 'processing') {
      wiscrackIndex.value = 0;
      wiscrackTimer = setInterval(() => {
        wiscrackIndex.value = (wiscrackIndex.value + 1) % wisecracks.length;
      }, 3000);
    } else {
      if (wiscrackTimer) {
        clearInterval(wiscrackTimer);
        wiscrackTimer = null;
      }
    }
  }
);

const wordCount = computed(() => countWords(chapterText.value));

const isValidWordCount = computed(() => wordCount.value >= MIN_WORDS && wordCount.value <= MAX_WORDS);

// Auto-populate title from the first heading in the pasted text, unless the user has typed their own
watchEffect(() => {
  if (userEditedTitle.value) return;
  const firstLine = chapterText.value.trimStart().split('\n')[0] || '';
  const headingMatch = firstLine.match(/^#{1,3}\s+(.+)/);
  chapterTitle.value = headingMatch?.[1]?.trim() ?? '';
});

const wordCountClass = computed(() => {
  if (wordCount.value === 0) return '';
  if (wordCount.value < MIN_WORDS) return 'under';
  if (wordCount.value > MAX_WORDS) return 'over';
  return 'ok';
});

// Step 1: store text in DO, then gate on auth before spending any compute
async function submit() {
  submitError.value = '';
  submitting.value = true;

  try {
    const res = await fetch('/api/chapter-roast/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: chapterText.value, title: chapterTitle.value || undefined }),
    });

    const data = (await res.json()) as { projectId?: string; error?: string };

    if (!res.ok || !data.projectId) {
      submitError.value = data.error || 'Something went wrong. Please try again.';
      submitting.value = false;
      return;
    }

    localStorage.setItem(LS_KEY, data.projectId);

    track('roast_submitted', {
      project_id: data.projectId,
      word_count: wordCount.value,
      signed_in: authStore.isAuthenticated,
      via: localStorage.getItem(LS_VIA_KEY) || undefined,
    });

    if (authStore.isAuthenticated) {
      // Already signed in — start immediately
      await startAndProcess(data.projectId);
    } else {
      // Show countdown, then redirect to auth
      startCountdown();
    }
  } catch {
    submitError.value = 'Network error. Please try again.';
    submitting.value = false;
  }
}

function startCountdown() {
  countdown.value = 3;
  state.value = 'countdown';
  countdownTimer = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(countdownTimer!);
      countdownTimer = null;
      router.push({ name: 'auth', query: { redirect: '/chapter-roast', context: 'chapter-roast' } });
    }
  }, 1000);
}

// Step 2: start processing (requires auth; also writes project to D1)
async function startAndProcess(pid: string) {
  projectId.value = pid;
  state.value = 'processing';

  let data: { ok?: boolean; status?: string; error?: string } = {};

  try {
    const res = await fetch(`/api/projects/${pid}/start-chapter-roast`, {
      method: 'POST',
      headers: { ...(await authStore.getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        via: localStorage.getItem(LS_VIA_KEY) || undefined,
        attribution: getAttribution(),
        touches: getTouches(),
      }),
    });

    data = (await res.json().catch(() => ({}))) as { ok?: boolean; status?: string; error?: string };

    if (!res.ok) {
      if (res.status === 404) {
        // Project expired — drop the stale ID and let them start fresh
        clearPending();
        state.value = 'input';
        submitError.value = 'That session expired. Paste your chapter again.';
        return;
      }
      fatalError.value = data?.error || 'Failed to start processing. Please try again.';
      state.value = 'error';
      return;
    }
  } catch {
    fatalError.value = 'Network error. Please try again.';
    state.value = 'error';
    return;
  }

  // If already complete (e.g. they're returning after processing finished), go straight to edit
  if (data?.status === 'complete' || data?.status === 'finalizing') {
    clearPending();
    router.replace(`/edit/${pid}`);
    return;
  }

  connectWebSocket(pid);
}

function connectWebSocket(pid: string) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  ws = new WebSocket(`${protocol}//${window.location.host}/api/projects/${pid}/ws`);

  ws.onopen = () => {
    ws!.send(JSON.stringify({ type: 'anon_sync' }));
  };

  ws.onmessage = event => {
    try {
      const msg = JSON.parse(event.data);
      if (msg.type === 'steps_start') {
        const step = msg.steps[0];
        if (step) stepName.value = step.name;
      } else if (msg.type === 'step_progress') {
        // wisecracks handle the messaging
      } else if (msg.type === 'all_complete') {
        ws?.close(1000);
        ws = null;
        clearPending();
        router.replace(`/edit/${pid}`);
      } else if (msg.type === 'error') {
        ws?.close(1000);
        ws = null;
        fatalError.value = 'Processing failed. Please try again.';
        state.value = 'error';
      }
    } catch {
      // ignore parse errors
    }
  };

  ws.onerror = () => {
    fatalError.value = 'Lost connection. Please refresh and try again.';
    state.value = 'error';
  };
}

function reset() {
  state.value = 'input';
  chapterText.value = '';
  chapterTitle.value = '';
  userEditedTitle.value = false;
  projectId.value = null;
  submitError.value = '';
  fatalError.value = '';
  stepName.value = '';
}

// Called on mount and when auth resolves — picks up a pending project after auth redirect
async function maybeStart() {
  const pending = localStorage.getItem(LS_KEY);
  if (pending && authStore.isAuthenticated) {
    await startAndProcess(pending);
  }
}

onMounted(() => {
  captureVia();
  void loadReviews();
  if (!authStore.loading) maybeStart();
});

// Handles the case where Firebase auth state resolves after mount
watch(
  () => authStore.isAuthenticated,
  authed => {
    if (authed) maybeStart();
  }
);

onUnmounted(() => {
  ws?.close(1000);
  ws = null;
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
  if (wiscrackTimer) {
    clearInterval(wiscrackTimer);
    wiscrackTimer = null;
  }
});
</script>

<style scoped>
.chapter-roast-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 24px;
}

.state-input,
.state-processing,
.state-gate,
.state-error {
  width: 100%;
  max-width: 720px;
}

/* Input state */
.hero {
  margin-bottom: 32px;
}

.hero h1 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 12px;
  line-height: 1.2;
}

.subtitle {
  color: var(--color-text-secondary, #666);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
}

/* Trust bar */
.trust-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  font-size: 0.85rem;
  color: var(--color-text-secondary, #666);
  margin-bottom: 28px;
}

.trust-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.trust-item :deep(.qicon) {
  color: var(--color-accent, #61aed4);
}

/* Reviews */
.reviews {
  width: 100vw;
  margin-top: 40px;
  margin-left: calc(50% - 50vw);
  background: var(--color-bg-secondary, #f8f9fa);
  padding: 40px 0 36px;
  overflow: hidden;
}

.reviews-heading {
  font-size: 0.875rem;
  font-weight: 600;
  color: #24292f;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1.4rem;
}

/* Fade the cards out at both edges so the rows read as continuous */
.marquee {
  -webkit-mask-image: linear-gradient(to right, transparent, #000 7%, #000 93%, transparent);
  mask-image: linear-gradient(to right, transparent, #000 7%, #000 93%, transparent);
  overflow: hidden;
}

.marquee + .marquee {
  margin-top: 12px;
}

.marquee-track {
  display: flex;
  gap: 12px;
  width: max-content;
  animation: marquee-slide 70s linear infinite;
}

.marquee-track.reverse {
  animation-duration: 82s;
  animation-direction: reverse;
}

@keyframes marquee-slide {
  from {
    transform: translateX(0);
  }
  to {
    /* the row is rendered twice; -50% (less half the gap) lands on the seam */
    transform: translateX(calc(-50% - 6px));
  }
}

.reviews:hover .marquee-track,
.reviews:focus-within .marquee-track {
  animation-play-state: paused;
}

.review-card {
  flex: 0 0 auto;
  width: 300px;
  box-sizing: border-box;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.9rem 1.05rem;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.reviews-footnote {
  font-size: 0.78rem;
  color: var(--color-text-muted, #8b949e);
  text-align: center;
  margin: 22px 0 0;
}

@media (max-width: 640px) {
  .review-card {
    width: 260px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .marquee {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .marquee::-webkit-scrollbar {
    display: none;
  }

  .marquee-track {
    animation: none;
    padding: 0 24px;
  }

  .review-card {
    scroll-snap-align: center;
  }

  /* the duplicate pass exists only to loop the animation */
  .marquee-track [aria-hidden='true'] {
    display: none;
  }
}

.review-quote {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #4a5568;
  margin: 0;
  font-style: normal;
}

.review-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1a202c;
  font-style: normal;
  letter-spacing: 0.01em;
}

.title-area {
  margin-bottom: 12px;
}

.title-input {
  width: 100%;
  padding: 10px 14px;
  font-size: 0.95rem;
  border: 1.5px solid var(--color-border, #ddd);
  border-radius: 6px;
  font-family: inherit;
  background: var(--color-bg, #fff);
  color: var(--color-text, #111);
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.title-input:focus {
  outline: none;
  border-color: var(--color-primary, #000);
}

.paste-area {
  margin-bottom: 24px;
}

.chapter-textarea {
  width: 100%;
  min-height: 320px;
  padding: 16px;
  font-size: 0.95rem;
  line-height: 1.6;
  border: 1.5px solid var(--color-border, #ddd);
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
  background: var(--color-bg, #fff);
  color: var(--color-text, #111);
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.chapter-textarea:focus {
  outline: none;
  border-color: var(--color-primary, #000);
}

.chapter-textarea.over-limit {
  border-color: #e53e3e;
}

.chapter-textarea.under-min {
  border-color: #dd6b20;
}

.word-count-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 0.85rem;
}

.word-count {
  font-weight: 500;
  color: var(--color-text-secondary, #666);
}

.word-count.ok {
  color: #38a169;
}

.word-count.over {
  color: #e53e3e;
}

.word-count.under {
  color: #dd6b20;
}

.word-count-hint {
  color: var(--color-text-tertiary, #999);
}

.word-count-hint.over {
  color: #e53e3e;
}

.word-count-hint.ok {
  color: #38a169;
}

.submit-area {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.roast-btn {
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  background: var(--color-primary, #000);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s;
}

.roast-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.roast-btn:not(:disabled):hover {
  opacity: 0.85;
}

.submit-area .roast-btn {
  width: 100%;
  padding: 16px 28px;
  font-size: 1.05rem;
  font-weight: 700;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition:
    opacity 0.15s,
    transform 0.15s,
    box-shadow 0.15s;
}

.submit-area .roast-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.error-msg {
  color: #e53e3e;
  font-size: 0.9rem;
  margin: 0;
}

.submit-legal {
  font-size: 0.8rem;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

.submit-legal a {
  color: #718096;
  text-decoration: underline;
}

.submit-legal a:hover {
  color: #4a5568;
}

.faq-heading {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 1rem;
}

/* Processing state */
.state-processing {
  display: flex;
  justify-content: center;
  padding-top: 80px;
}

.processing-inner {
  text-align: center;
  max-width: 480px;
}

.t-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 36px;
}

.t-mark {
  display: block;
  height: 80px;
  width: auto;
  transform-origin: 50% 85%;
  animation: t-nervous 4s ease-in-out infinite;
  color: var(--color-text-primary, #24292f);
}

.t-shadow {
  width: 44px;
  height: 10px;
  background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.22) 0%, transparent 70%);
  border-radius: 50%;
  margin-top: 4px;
  animation: t-shadow 4s ease-in-out infinite;
}

@keyframes t-nervous {
  /* rest */
  0% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  /* quick head shake */
  5% {
    transform: rotate(9deg) translateY(0) scaleX(1) scaleY(1);
  }
  10% {
    transform: rotate(-8deg) translateY(0) scaleX(1) scaleY(1);
  }
  15% {
    transform: rotate(7deg) translateY(0) scaleX(1) scaleY(1);
  }
  20% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  /* small excited hop */
  26% {
    transform: rotate(0deg) translateY(3px) scaleX(1.06) scaleY(0.9);
  }
  32% {
    transform: rotate(-3deg) translateY(-12px) scaleX(0.93) scaleY(1.1);
  }
  38% {
    transform: rotate(0deg) translateY(2px) scaleX(1.06) scaleY(0.9);
  }
  42% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  /* pause... then surprise big jump */
  48% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  53% {
    transform: rotate(0deg) translateY(5px) scaleX(1.1) scaleY(0.85);
  }
  60% {
    transform: rotate(-5deg) translateY(-22px) scaleX(0.88) scaleY(1.18);
  }
  67% {
    transform: rotate(0deg) translateY(4px) scaleX(1.12) scaleY(0.84);
  }
  72% {
    transform: rotate(0deg) translateY(-8px) scaleX(0.95) scaleY(1.07);
  }
  76% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  /* more wiggles to end */
  82% {
    transform: rotate(-6deg) translateY(0) scaleX(1) scaleY(1);
  }
  87% {
    transform: rotate(6deg) translateY(0) scaleX(1) scaleY(1);
  }
  92% {
    transform: rotate(-3deg) translateY(0) scaleX(1) scaleY(1);
  }
  100% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
}

@keyframes t-shadow {
  /* rest / shakes — shadow stays normal */
  0% {
    transform: scaleX(1);
    opacity: 1;
  }
  20% {
    transform: scaleX(1);
    opacity: 1;
  }
  /* small hop: wind up → air → land */
  26% {
    transform: scaleX(1.1);
    opacity: 1;
  }
  32% {
    transform: scaleX(0.55);
    opacity: 0.35;
  }
  38% {
    transform: scaleX(1.2);
    opacity: 1;
  }
  42% {
    transform: scaleX(1);
    opacity: 1;
  }
  /* big jump: wind up → way up → heavy land → bounce */
  48% {
    transform: scaleX(1);
    opacity: 1;
  }
  53% {
    transform: scaleX(1.12);
    opacity: 1;
  }
  60% {
    transform: scaleX(0.3);
    opacity: 0.15;
  }
  67% {
    transform: scaleX(1.3);
    opacity: 1;
  }
  72% {
    transform: scaleX(0.75);
    opacity: 0.7;
  }
  76% {
    transform: scaleX(1);
    opacity: 1;
  }
  /* wiggles / settle */
  100% {
    transform: scaleX(1);
    opacity: 1;
  }
}

.wisecrack {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0 0 24px;
  line-height: 1.4;
  min-height: 2.8em;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wisecrack-enter-active,
.wisecrack-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}
.wisecrack-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.wisecrack-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.step-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: var(--color-text-secondary, #666);
  background: var(--color-bg-subtle, #f5f5f5);
  padding: 6px 14px;
  border-radius: 20px;
}

.step-dot {
  width: 6px;
  height: 6px;
  background: var(--color-primary, #000);
  border-radius: 50%;
  animation: pulse 1.2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

/* Countdown state */
.state-countdown {
  display: flex;
  justify-content: center;
  padding-top: 120px;
}

.countdown-inner {
  text-align: center;
  max-width: 480px;
}

.countdown-message {
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.5;
  margin: 0 0 20px;
  color: var(--color-text, #111);
}

.countdown-sub {
  font-size: 1rem;
  color: var(--color-text-secondary, #666);
  margin: 0;
}

.countdown-sub strong {
  font-size: 1.4rem;
  color: var(--color-text, #111);
  font-variant-numeric: tabular-nums;
}

/* Error state */
.state-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 80px;
  gap: 16px;
}

.state-error h2 {
  margin: 0;
}

.state-error p {
  color: var(--color-text-secondary, #666);
  margin: 0;
}

/* FAQ */
.faq {
  margin-top: 48px;
}

.faq-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 8px;
  overflow: hidden;
}

.faq-q {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  gap: 16px;
}

.faq-q:hover {
  background: #f7fafc;
}

.faq-chevron {
  font-size: 18px;
  flex-shrink: 0;
  color: #a0aec0;
  line-height: 1;
}

.faq-a {
  display: none;
  padding: 0 1.5rem 1rem 1.5rem;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #4a5568;
}

.faq-item.open .faq-a {
  display: block;
}
</style>
